/*
 * Lemon Labels — Label Finder
 * Two-step picker: manufacturer -> model -> deep-link into a pre-filtered collection.
 * No external API, no credentials. Data comes from a theme JSON asset.
 *
 * Config via data-attributes on <ll-label-finder>:
 *   data-json-url        URL of the manufacturer/model JSON asset
 *   data-collection-url  Base results URL, e.g. /collections/all
 *   data-filter-param    Storefront filter key for printer model,
 *                        e.g. filter.p.m.spec.printer_model
 */
if (!customElements.get('ll-label-finder')) {
  class LabelFinder extends HTMLElement {
    constructor() {
      super();
      this.data = [];
      this.selectedManufacturer = null;
    }

    async connectedCallback() {
      this.jsonUrl = this.dataset.jsonUrl;
      this.collectionUrl = (this.dataset.collectionUrl || '/collections/all').split('?')[0];
      this.filterParam = this.dataset.filterParam || 'filter.p.m.spec.printer_model';

      this.els = {
        manufacturers: this.querySelector('[data-role="manufacturers"]'),
        models: this.querySelector('[data-role="models"]'),
        panelMfr: this.querySelector('[data-step="manufacturer"]'),
        panelModel: this.querySelector('[data-step="model"]'),
        mfrName: this.querySelector('[data-role="mfr-name"]'),
        back: this.querySelector('[data-role="back"]'),
        mfrSearch: this.querySelector('[data-role="mfr-search"]'),
        modelSearch: this.querySelector('[data-role="model-search"]'),
        status: this.querySelector('[data-role="status"]'),
        confirm: this.querySelector('[data-role="confirm"]'),
        confirmModel: this.querySelector('[data-role="confirm-model"]'),
        changeBtn: this.querySelector('[data-role="change"]'),
        finding: this.querySelector('[data-role="finding"]'),
      };

      if (this.els.back) {
        this.els.back.addEventListener('click', () => this.showStep('manufacturer'));
      }
      if (this.els.changeBtn) {
        this.els.changeBtn.addEventListener('click', () => {
          if (this.els.confirm) this.els.confirm.hidden = true;
          this.showStep('manufacturer');
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      if (this.els.mfrSearch) {
        this.els.mfrSearch.addEventListener('input', (e) =>
          this.filterButtons(this.els.manufacturers, e.target.value));
      }
      if (this.els.modelSearch) {
        this.els.modelSearch.addEventListener('input', (e) =>
          this.filterButtons(this.els.models, e.target.value));
      }

      try {
        const res = await fetch(this.jsonUrl);
        if (!res.ok) throw new Error('Data load failed: ' + res.status);
        this.data = await res.json();
        this.renderManufacturers();
        this.checkActiveFilter();
      } catch (err) {
        console.error('[ll-label-finder]', err);
        if (this.els.status) {
          this.els.status.textContent = 'Could not load the printer list. Please try again later.';
        }
      }
    }

    renderManufacturers() {
      const frag = document.createDocumentFragment();
      this.data
        .slice()
        .sort((a, b) => a.manufacturer.localeCompare(b.manufacturer))
        .forEach((row) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'llf__chip';
          btn.textContent = row.manufacturer;
          btn.addEventListener('click', () => this.selectManufacturer(row));
          frag.appendChild(btn);
        });
      this.els.manufacturers.innerHTML = '';
      this.els.manufacturers.appendChild(frag);
    }

    selectManufacturer(row) {
      this.selectedManufacturer = row;
      if (this.els.mfrName) this.els.mfrName.textContent = row.manufacturer;
      if (this.els.modelSearch) this.els.modelSearch.value = '';

      const frag = document.createDocumentFragment();
      row.models
        .slice()
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .forEach((model) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'llf__chip';
          btn.textContent = model;
          btn.addEventListener('click', () => this.goToResults(model));
          frag.appendChild(btn);
        });
      this.els.models.innerHTML = '';
      this.els.models.appendChild(frag);

      this.showStep('model');
      if (this.els.modelSearch) this.els.modelSearch.focus();
    }

    goToResults(model) {
      // Flag that this navigation came from the finder, so the destination
      // (a filtered collection) knows to jump the user down to the results.
      try { sessionStorage.setItem('ll-finder-jump', '1'); } catch (e) {}
      // Immediate visual cue while the page navigates.
      if (this.els.panelMfr) this.els.panelMfr.hidden = true;
      if (this.els.panelModel) this.els.panelModel.hidden = true;
      if (this.els.finding) this.els.finding.hidden = false;
      const url = this.collectionUrl + '?' +
        encodeURIComponent(this.filterParam) + '=' + encodeURIComponent(model);
      window.location.href = url;
    }

    // When the collection is already filtered to a printer, collapse the picker
    // into a confirmation and (if the user came from the finder) scroll to results.
    checkActiveFilter() {
      let active = null;
      try { active = new URLSearchParams(location.search).get(this.filterParam); } catch (e) {}
      if (!active) return;

      let label = active;
      const mfr = this.data.find((r) => (r.models || []).some((m) => m === active));
      if (mfr) label = mfr.manufacturer + ' ' + active;
      this.showConfirm(label);

      let jumped = false;
      try { jumped = sessionStorage.getItem('ll-finder-jump') === '1'; } catch (e) {}
      if (jumped) {
        try { sessionStorage.removeItem('ll-finder-jump'); } catch (e) {}
        this.scrollToResults();
      }
    }

    showConfirm(label) {
      if (this.els.panelMfr) this.els.panelMfr.hidden = true;
      if (this.els.panelModel) this.els.panelModel.hidden = true;
      if (this.els.finding) this.els.finding.hidden = true;
      if (this.els.confirmModel) this.els.confirmModel.textContent = label;
      if (this.els.confirm) this.els.confirm.hidden = false;
      this.setAttribute('data-active-step', 'confirm');
    }

    scrollToResults() {
      // Let the page lay out, then bring the confirmation + results into view.
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          var target = this.els.confirm && !this.els.confirm.hidden
            ? this.els.confirm
            : document.querySelector('.product-grid, results-list, .collection__products, [id*="product-grid"]');
          if (target && target.scrollIntoView) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 80);
      });
    }

    filterButtons(container, value) {
      const q = (value || '').trim().toLowerCase();
      Array.from(container.children).forEach((btn) => {
        const match = btn.textContent.toLowerCase().includes(q);
        btn.style.display = match ? '' : 'none';
      });
    }

    showStep(step) {
      const isModel = step === 'model';
      this.els.panelMfr.hidden = isModel;
      this.els.panelModel.hidden = !isModel;
      this.setAttribute('data-active-step', step);
    }
  }
  customElements.define('ll-label-finder', LabelFinder);
}
